import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Account, Id } from '@hero/schema';
import { AccountBalanceCard } from '../BalanceCard/AccountBalanceCard';
import { AccountInvoicesTab } from '../Drawer/AccountInvoicesTab';
import { AccountTransactionsTab } from '../Drawer/AccountTransactionsTab';
import { AccountSummaryTab } from '../Drawer/AccountSummaryTab';

/**
 * `'auto'` is intentionally NOT a valid `AccountPage` value — the
 * host is closer to the viewer's identity than the page is, so
 * resolve the bilateral role upstream and pass the concrete side
 * here.
 */
export type AccountPageAsRole = 'buyer' | 'seller';

export interface AccountPageProps {
  /** Resolved Account doc (e.g. from `useAccountByPairApi`). */
  account: Account;
  /** Viewer org id — drives all "you owe / owed to you" framing. */
  viewerOrgId?: Id | string;
  /**
   * Viewer's role on the embedded `InvoiceTableManager`. Defaults
   * to `seller` to surface "Upload proof" actions on the carrier-
   * dashboard CRM context (the larger of the two consumers today).
   */
  asRole?: AccountPageAsRole;
  /**
   * Optional override for the counterparty name in the balance
   * card header — useful when the host has a richer Client name
   * than the snapshot stored on the account.
   */
  counterpartyNameOverride?: string;
  /**
   * Counterparty CRM Client id used to scope the "Create invoice
   * from delivered legs" picker rendered inside
   * `AccountInvoicesTab`. Threaded down from the host so the
   * picker can narrow eligible legs to this one buyer.
   */
  counterpartyClientId?: Id;
  className?: string;
}

const asRoleToDisplayForUser = (role: AccountPageAsRole): 'customer' | 'carrier' =>
  role === 'buyer' ? 'customer' : 'carrier';

/**
 * Mid-level page surface for a single bilateral Account.
 *
 * Used by:
 * - the CRM Invoices tab (`AccountInvoicesTabContent` mounts this
 *   once a by-pair lookup resolves), and
 * - the optional `/accounts/:id` route (phase 2).
 *
 * NOT a top-level route component — it expects to be embedded.
 * The host page is responsible for mounting `<AccountDrawer />`,
 * `<AccountTransactionDrawer />`, and `<InvoiceDrawer />` once.
 */
export function AccountPage({
  account,
  viewerOrgId,
  asRole = 'seller',
  counterpartyNameOverride,
  counterpartyClientId,
  className,
}: AccountPageProps) {
  const { t } = useTranslation(['m-account']);
  const [activeTab, setActiveTab] = useState<'invoices' | 'transactions' | 'info'>('invoices');

  const tabs: { id: typeof activeTab; label: string }[] = [
    {
      id: 'invoices',
      label: t('page.tabs.invoices', { defaultValue: 'Invoices' }),
    },
    {
      id: 'transactions',
      label: t('page.tabs.transactions', { defaultValue: 'Transactions' }),
    },
    {
      id: 'info',
      label: t('page.tabs.info', { defaultValue: 'Info' }),
    },
  ];

  return (
    <div className={`flex flex-col gap-4 ${className ?? ''}`}>
      <AccountBalanceCard
        account={account}
        viewerOrgId={viewerOrgId}
        counterpartyNameOverride={counterpartyNameOverride}
      />

      <div className="flex border-b border-gray-100 px-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-primary border-primary'
                : 'text-muted-foreground border-transparent hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0">
        {activeTab === 'invoices' && (
          <AccountInvoicesTab
            account={account}
            displayForUser={asRoleToDisplayForUser(asRole)}
            counterpartyClientId={counterpartyClientId}
            viewerOrgId={viewerOrgId as Id | undefined}
          />
        )}
        {activeTab === 'transactions' && <AccountTransactionsTab account={account} />}
        {activeTab === 'info' && (
          <AccountSummaryTab account={account} viewerOrgId={viewerOrgId} />
        )}
      </div>
    </div>
  );
}
